import React from 'react'
import Slider from '@mui/material/Slider'
import { find, isNaN, result, debounce } from 'libraries/lodash'
import connect from 'libraries/front-end-core/decorators/connect'
import { addCommas } from 'helpers/transforms'
import { getStats } from 'services/DealwatchService'
import FilterComponent from './FilterComponent'

const STEP_SHIFT = 1e2
const LT_OPERATOR = '<='
const GT_OPERATOR = '>='
const DEBOUNCE_TIME = 300

export default connect(({ name: parameterName }) => ({
  stats: getStats({ parameterName })
}))(props => <NumberFilter {...props} />)

function formatNumber (value, format) {
  if (format) {
    switch (format.type) {
      case 'Currency':
        return format.symbol + addCommas(value)
      case 'Percentage':
        return value + '%'
    }
  } else {
    return addCommas(value)
  }
}

class NumberFilter extends FilterComponent {
  state = {
    min: '',
    max: '',
    sliderValue: []
  }

  constructor (props) {
    super(props)
    this.state = this.stateFromProps(props)
  }

  stateFromProps (props) {
    const [min, max] = this.getValues(props, false)
    const [sliderMin, sliderMax] = this.getValues()
    const state = {
      min: !isNaN(+min) ? min.toString() : '',
      max: !isNaN(+max) ? max.toString() : '',
      sliderValue: [sliderMin, sliderMax]
    }
    return state
  }

  get statsAvailable () {
    const aggr = result(this.props.stats, [this.props.name, 'results'], null)
    return aggr !== null
  }

  get range () {
    const aggr = result(this.props.stats, [this.props.name, 'results'], null)
    if (aggr === null) {
      switch (this.props.format && this.props.format.type) {
        case 'Percentage':
          return [0, 10e1]
        default:
          return [0, 10e10]
      }
    }
    const { pow, log10, ceil, floor } = Math
    const [key] = Object.keys(aggr)
    const { min, max } = aggr[key]
    const lower = min < 1 ? 0 : pow(10, floor(log10(min)))
    const upper = max < 1 ? 1 : pow(10, ceil(log10(max)))
    return [lower, upper]
  }

  handleSliderChange = (event, value) => {
    let [min, max] = value
    min = this.convertFromStepValue(min)
    max = this.convertFromStepValue(max)
    this.setState({
      sliderValue: [min, max]
    })
    // wait for 300ms before updating the filterValue
    this.debouncedSliderChange()
  }

  debouncedSliderChange = debounce(() => {
    const [min, max] = this.state.sliderValue
    this.props.setFilterValue([
      [[min.toString()], GT_OPERATOR],
      [[max.toString()], LT_OPERATOR]
    ])
  }, DEBOUNCE_TIME)

  getValues (props = this.props, defaultToExtremes = true) {
    const [min, max] = this.range
    const lower = find(props.filterValue, ({ operator }) => operator === GT_OPERATOR)
    const upper = find(props.filterValue, ({ operator }) => operator === LT_OPERATOR)
    const values = defaultToExtremes
      ? [
          +result(lower, 'value[0]', min),
          +result(upper, 'value[0]', max)
        ]
      : [
          +result(lower, 'value[0]'),
          +result(upper, 'value[0]')
        ]
    return values
  }

  convertToStepValue (value) {
    const { log10, floor, max } = Math
    if (value >= 1) {
      const base = floor(log10(value)) * 9 + 1
      const position = +value.toString()[0] - 1
      return STEP_SHIFT + base + position
    } else {
      return max(0, floor(value * 10)) - 10 + STEP_SHIFT
    }
  }

  convertFromStepValue (value) {
    const { pow, floor } = Math
    value = Math.max(value - STEP_SHIFT, -10)
    if (value > 0) {
      const position = 1 + (value - 1) % 9
      const base = pow(10, floor((value - 1) / 9))
      return position * base
    } else {
      return +(1 + value / 10).toFixed(1)
    }
  }

  UNSAFE_componentWillReceiveProps (nextProps, _) { // eslint-disable-line
    this.setState(this.stateFromProps(nextProps))
  }

  handleNumericChange = (param) => ({ target: { value } }) => {
    this.setState({ [param]: value })
  }

  handleBlur = () => {
    const { min, max } = this.state
    if (min && !isNaN(+min) && max && !isNaN(+max) && +min > +max) {
      this.setState({ max: min, min: max })
      this.props.setFilterValue([
        [[(+max).toString()], GT_OPERATOR],
        [[(+min).toString()], LT_OPERATOR]
      ])
    } else {
      this.props.setFilterValue([
        min && !isNaN(+min) && [[(+min).toString()], GT_OPERATOR],
        max && !isNaN(+max) && [[(+max).toString()], LT_OPERATOR]
      ].filter(x => !!x))
    }
  }

  renderNumeric = (lower, upper) => (
    <div className='flex items-center flex-wrap'>
      <span className='mr6'>Min:</span>
      <input
        className='min-w13 bg-transparent ba b--black ml0 mr6 mv4 pv2 ph4 f5'
        type='number'
        placeholder={lower.toString()}
        onChange={this.handleNumericChange('min')}
        onBlur={this.handleBlur}
        value={this.state.min}
        min={lower.toString()}
      />
      <span className='mr6'>Max:</span>
      <input
        className='min-w13 bg-transparent ba b--black mv2 mh0 pv2 ph4 mv4 f5'
        type='number'
        placeholder={upper.toString()}
        onChange={this.handleNumericChange('max')}
        onBlur={this.handleBlur}
        value={this.state.max}
        max={upper.toString()}
      />
    </div>
  )

  render () {
    const { log10 } = Math
    const [min, max] = this.range
    const stepMin = min === 0 ? STEP_SHIFT - 10 : log10(min) * 9 + 1 + STEP_SHIFT
    const stepMax = log10(max) * 9 + 1 + STEP_SHIFT
    const [sliderMin, sliderMax] = this.state.sliderValue
    const stepLower = this.convertToStepValue(sliderMin)
    const stepUpper = this.convertToStepValue(sliderMax)

    return !this.statsAvailable
      ? (<div>N/A</div>)
      : !this.props.basicDisplay
          ? this.renderNumeric(min, max)
          : (
            <div className='mt4'>
              <div className='mh5'>
                <Slider
                  min={stepMin}
                  max={stepMax}
                  value={[stepLower, stepUpper]}
                  onChange={this.handleSliderChange}
                  classes={{ root: 'cadet-blue', thumb: 'white ba b--cadet-blue h1 w6' }}
                />
              </div>
              <div className='flex justify-between'>
                <div>{formatNumber(sliderMin, this.props.format)}</div>
                <div>{formatNumber(sliderMax, this.props.format)}</div>
              </div>
            </div>
            )
  }
}
